import { useEffect, useMemo, useRef, useState } from 'react';
import { classifyClientSearchInput } from '../../services/utils/stringUtils';
import { clientService, getCnpjSearchUserMessage } from '../../services/clients/ClientService';
import { isCanceledError } from '../../services/utils/apiErrorUtils';
import { fetchTimClientData, type TimClientData } from '../../services/timData/timDataService';
import type { ClientResponse } from '../../types/dtos';

export const CNPJ_SEARCH_TEXT_ERROR = 'A consulta CNPJ.ws aceita apenas CNPJ (14 dígitos). Para buscar por razão social, use a lista de clientes cadastrados.';

interface ClientCnpjSearchOptions {
  query: string;
  isModalOpen: boolean;
  loadClients: () => Promise<void> | void;
  onClientFound: (client: ClientResponse) => void;
  onTimData: (data: TimClientData | null) => void;
  setError: (message: string) => void;
}

export function useClientCnpjSearch({ query, isModalOpen, loadClients, onClientFound, onTimData, setError }: ClientCnpjSearchOptions) {
  const [validation, setValidation] = useState<{ kind: 'length' | 'text'; query: string } | null>(null);
  const [searchingCnpj, setSearchingCnpj] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const input = useMemo(() => classifyClientSearchInput(query), [query]);

  useEffect(() => {
    setValidation((previous) =>
      previous?.kind === 'length' && input.kind === 'cnpj' && !input.valid ? previous : null
    );
  }, [input]);

  useEffect(() => {
    setValidation(null);
    if (!isModalOpen) setSearchingCnpj(false);
    return () => {
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, [isModalOpen]);

  const validate = (consult: boolean) => {
    if (input.kind === 'cnpj' && !input.valid) {
      setValidation({ kind: 'length', query });
    } else if (consult && input.kind === 'text') {
      setValidation({ kind: 'text', query });
    } else if (input.kind !== 'text' || consult) {
      setValidation(null);
    }
  };

  let cnpjInputError = '';
  if (validation?.kind === 'length' && input.kind === 'cnpj' && !input.valid) {
    cnpjInputError = `CNPJ deve ter 14 dígitos (você digitou ${input.digits.length}).`;
  } else if (validation?.kind === 'text' && validation.query === query && input.kind === 'text') {
    cnpjInputError = CNPJ_SEARCH_TEXT_ERROR;
  }

  const handleSearchCnpj = async () => {
    setError('');
    validate(true);
    if (input.kind !== 'cnpj' || !input.valid) return;

    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setSearchingCnpj(true);
    try {
      // A falha TIM não impede a seleção do cliente retornado pela Receita.
      const timLookup = fetchTimClientData(input.digits).then((data) => {
        if (!ac.signal.aborted) onTimData(data.found ? data : null);
      }).catch((error: unknown) => {
        if (ac.signal.aborted) return;
        console.warn('Consulta de dados TIM falhou (não bloqueante):', error);
        onTimData(null);
      });
      const [response] = await Promise.all([
        clientService.searchAndSaveClient(input.digits, { signal: ac.signal }),
        timLookup,
      ]);
      if (ac.signal.aborted) return;
      if (response.success && response.data) {
        await loadClients();
        if (ac.signal.aborted) return;
        onClientFound(response.data);
        setError('');
      } else {
        setError(response.message || 'Cliente não encontrado ou CNPJ inválido');
      }
    } catch (error: unknown) {
      if (ac.signal.aborted || isCanceledError(error)) return;
      const message = getCnpjSearchUserMessage(error);
      if (message) {
        setError(message);
        console.error('Erro ao buscar CNPJ:', error);
      }
    } finally {
      if (abortRef.current === ac) {
        abortRef.current = null;
        setSearchingCnpj(false);
      }
    }
  };

  return { cnpjInputError, searchingCnpj, handleClientSearchBlur: () => validate(false), handleSearchCnpj };
}
